/**
 * OS notifications (`03-client-app.md` section 7).
 *
 * Two rules from the product side shape this:
 *
 * - An OS notification only appears while the app is running. Nothing is scheduled ahead of
 *   time any more. The one thing that was, the play session reminder, went with that feature.
 * - A type the user turned off in settings never becomes an OS notification, though it still
 *   appears in the feed.
 */

import * as React from 'react';
import { useNotifications } from '../api/hooks.js';
import { capabilities } from '../platform/index.js';
import { useSettings } from '../lib/settings.js';

const SEEN_KEY = 'gb.notifications.lastSeenId';

export function useOsNotifications(): void {
  const { data } = useNotifications();
  const { mutedNotifications } = useSettings();
  const lastSeen = React.useRef<string | null>(
    typeof localStorage === 'undefined' ? null : localStorage.getItem(SEEN_KEY),
  );

  React.useEffect(() => {
    if (!capabilities.osNotifications || !data?.items?.length) return;

    const newest = data.items[0]!;
    // First run: remember where we are without firing a notification for old items.
    if (!lastSeen.current) {
      lastSeen.current = newest.id;
      localStorage.setItem(SEEN_KEY, newest.id);
      return;
    }
    if (newest.id === lastSeen.current) return;

    // ULIDs sort by time, so everything above the last seen id is new.
    const fresh = data.items.filter((n) => n.id > lastSeen.current!);
    lastSeen.current = newest.id;
    localStorage.setItem(SEEN_KEY, newest.id);

    void showAll(fresh.filter((n) => !mutedNotifications.includes(n.type)));
  }, [data, mutedNotifications]);
}

/**
 * One OS notification, for the things that are not feed items.
 *
 * The app keeps running after its window closes, and anything it wants to say while it is in
 * the tray has to be said here: there is no screen to put it on.
 */
export async function notify(title: string, body: string): Promise<void> {
  if (!capabilities.osNotifications) return;
  await showAll([{ id: title, title, body }]);
}

async function showAll(items: Array<{ id: string; title: string; body: string }>): Promise<void> {
  if (items.length === 0) return;
  try {
    const { isPermissionGranted, requestPermission, sendNotification } =
      await import('@tauri-apps/plugin-notification');
    let granted = await isPermissionGranted();
    if (!granted) granted = (await requestPermission()) === 'granted';
    if (!granted) return;

    // Three at a time is enough to be useful; more than that is noise after a long time away.
    for (const item of items.slice(0, 3)) {
      sendNotification({ title: item.title, body: item.body });
    }
  } catch (err) {
    console.warn('could not show an OS notification', err);
  }
}
